import { useCallback, useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'

interface IdToken {
  claims?: {
    roles?: string[]
  }
  roles?: string[]
  rawIdToken?: string
}

export const useAuth = () => {
  const { instance, accounts } = useMsal()
  const [userRoles, setUserRoles] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Helper to extract roles from idToken (similar to UnifiedAuthContext)
  const extractRoles = useCallback((idToken: IdToken | null): string[] => {
    if (!idToken) return []
    
    // Try to get roles from claims, or directly from idToken.roles
    if (idToken.claims && Array.isArray(idToken.claims.roles)) {
      return idToken.claims.roles
    }
    if (Array.isArray(idToken.roles)) {
      return idToken.roles
    }
    
    // Try to get roles from rawIdToken if present
    if (idToken.rawIdToken) {
      try {
        const payload = JSON.parse(atob(idToken.rawIdToken.split('.')[1]))
        if (Array.isArray(payload.roles)) {
          return payload.roles
        }
      } catch (err) {
        console.error('Error parsing rawIdToken:', err)
      }
    }
    return []
  }, [])

  const getTokenResponse = useCallback(async () => {
    if (!accounts.length) return null

    const request = {
      scopes: [`api://${import.meta.env.VITE_AZURE_CLIENT_ID || '14d7b2b8-459b-452b-b413-452e691b6e74'}/access_as_user`],
      account: accounts[0],
    }

    try {
      const response = await instance.acquireTokenSilent(request)
      return response
    } catch (error) {
      console.error('Failed to get token response:', error)
      return null
    }
  }, [instance, accounts])

  const loadUserRoles = useCallback(async () => {
    setIsLoading(true)
    try {
      const tokenResponse = await getTokenResponse()
      if (tokenResponse && tokenResponse.idToken) {
        // Create the idToken object for extractRoles
        const idTokenObj: IdToken = {
          rawIdToken: tokenResponse.idToken
        }
        const roles = extractRoles(idTokenObj)
        setUserRoles(roles)
      } else {
        setUserRoles([])
      }
    } catch (error) {
      console.error('Failed to load user roles:', error)
      setUserRoles([])
    } finally {
      setIsLoading(false)
    }
  }, [getTokenResponse, extractRoles])

  const hasRole = useCallback((role: string): boolean => {
    return userRoles.includes(role)
  }, [userRoles])

  const isAdmin = useCallback((): boolean => {
    return hasRole('NtCdn.Admin')
  }, [hasRole])

  useEffect(() => {
    if (accounts.length > 0) {
      loadUserRoles()
    } else {
      setUserRoles([])
      setIsLoading(false)
    }
  }, [accounts, loadUserRoles])

  return {
    userRoles,
    hasRole,
    isAdmin,
    isLoading,
    refreshRoles: loadUserRoles
  }
}